import { AIConversationType } from '@ViewModels';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';

/**
 * Maximum allowed length for an AI agent template's objective prompt.
 * Mirrors the authoritative backend limit enforced in Levitate-API's
 * `AIAgentTemplateService.cs`. Keep this in sync with the backend to avoid
 * letting users save a prompt the API will reject.
 */
export const MAX_OBJECTIVE_PROMPT_LENGTH = 60000;

// Sales conversation types are managed in AIDA and hidden from admin
export const conversationTypes = [
	{ value: AIConversationType.BlogGeneration, label: 'Blog' },
	{ value: AIConversationType.Custom, label: 'Custom' },
	{ value: AIConversationType.EmailContentGeneration, label: 'Email Content' },
	{ value: AIConversationType.GoogleReviewReply, label: 'Google Review Reply' },
	{ value: AIConversationType.SiteWide, label: 'Site Wide' },
	{ value: AIConversationType.SocialMediaContentGeneration, label: 'Social Media Content Generation' },
	{ value: AIConversationType.SuccessMeetingPrep, label: 'Success Meeting Prep' },
	{ value: AIConversationType.Admin, label: 'Admin' },
];

// Categorize conversation types by required permissions
export const conversationTypePermissions: Partial<Record<AIConversationType, AdminPermission>> = {
	[AIConversationType.SalesPracticeCall]: AdminPermission.ModifySalesPrompts,
	[AIConversationType.SalesPostCallCoaching]: AdminPermission.ModifySalesPrompts,
	[AIConversationType.SalesDemoCallCoaching]: AdminPermission.ModifySalesPrompts,
	[AIConversationType.SuccessMeetingPrep]: AdminPermission.ModifySystemPrompts,
	[AIConversationType.BlogGeneration]: AdminPermission.ModifyContentGenerationPrompts,
	[AIConversationType.Custom]: AdminPermission.ModifySystemPrompts,
	[AIConversationType.EmailContentGeneration]: AdminPermission.ModifyContentGenerationPrompts,
	[AIConversationType.SocialMediaContentGeneration]: AdminPermission.ModifyContentGenerationPrompts,
	[AIConversationType.GoogleReviewReply]: AdminPermission.ModifySystemPrompts,
	[AIConversationType.SiteWide]: AdminPermission.ModifySystemPrompts,
	[AIConversationType.Admin]: AdminPermission.ModifySystemPrompts,
};

export function getFilteredConversationTypes(userPermissions: Record<AdminPermission, boolean>) {
	return conversationTypes.filter(conversationType => {
		const requiredPermission = conversationTypePermissions[conversationType.value];
		return userPermissions[requiredPermission] === true;
	});
}

export const availableModels = [
	// GPT-4o Family
	{ value: 'gpt-4o', label: 'GPT-4o' },
	{ value: 'gpt-4o-mini', label: 'GPT-4o Mini' },

	// GPT-4.1 Family
	{ value: 'gpt-4.1', label: 'GPT-4.1' },
	{ value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
	{ value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },

	// GPT-5 Family
	{ value: 'gpt-5', label: 'GPT-5' },
	{ value: 'gpt-5-mini', label: 'GPT-5 Mini' },
	{ value: 'gpt-5-nano', label: 'GPT-5 Nano' },

	// GPT-5.1 Family
	{ value: 'gpt-5.1', label: 'GPT-5.1' },

	// GPT-5.4 Family
	{ value: 'gpt-5.4', label: 'GPT-5.4' },
	{ value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
	{ value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano' },

	// GPT-5.5 Family
	{ value: 'gpt-5.5', label: 'GPT-5.5' },
	{ value: 'gpt-5.5-pro', label: 'GPT-5.5 Pro' },

	// GPT-5.6 Family
	{ value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol' },
	{ value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
	{ value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
];

export const verticalOptions = [
	{ value: 'Accounting', label: 'Accounting' },
	{ value: 'Auto Repair', label: 'Auto Repair' },
	{ value: 'Chiropractic', label: 'Chiropractic' },
	{ value: 'Dental', label: 'Dental' },
	{ value: 'Financial', label: 'Financial' },
	{ value: 'Home Maintenance', label: 'Home Maintenance' },
	{ value: 'Insurance', label: 'Insurance' },
	{ value: 'Legal', label: 'Legal' },
	{ value: 'Non-Profit', label: 'Non-Profit' },
];
